//! Import the necessary dependencies
import { API_URI } from "../../constants/consts";
import { api } from "../../services/apis/configs/axiosConfigs";

//* Type for pagination data
import { type Pagination } from "../../services/stores/types";

//* Import types from types
import {
  Question,
  QuestionDetails,
  CreateQuestionInput,
  UpdateQuestionInput,
  GetQuestionsResponse,
  GetQuestionsDisciplinesResponse,
} from "./types/types";
//? Import the QuestionType type from the form/types/types.tsx file
import { QuestionType } from "../../hooks/questions/types/types";


/**
 * Maps the response data from the API to a Question object.
 *
 * @param data - The raw data from the API response.
 * @returns A Question object with the mapped properties.
 */
export function mapQuestionResponse(data: any): Question {
  return {
    id: data.id,
    educationLevel: data.educationLevel,
    difficultyLevel: data.difficulty,
    discipline: data.discipline,
    createdBy: data.created_by,
    createdAt: data.created_at ? new Date(data.created_at) : new Date(),
  };
}


/**
 * Maps the response data to a QuestionDetails object.
 *
 * @param data - The response data to map.
 * @returns A QuestionDetails object containing the mapped data.
 */
export function mapQuestionDetailsResponse(data: any): QuestionDetails {
  return {
    id: data.id,
    educationLevel: data.educationLevel,
    difficulty: data.difficulty,
    statement: data.statement,
    discipline: data.discipline,
    subQuestions: data.subQuestions || [],
    tags: data.tags || [],
    createdAt: data.created_at ? new Date(data.createdAt) : new Date(),
    updatedAt: data.updated_at ? new Date(data.updatedAt) : new Date(),
    createdBy: data.created_by,
  };
}

/**
 * Maps the payload data to the API format.
 *
 * @param data - The data to map.
 * @returns The mapped payload data.
 */
export function mapQuestionPayload(data: any) {
  const payload: any = {
    education_level: data.education_level,
    difficulty: data.difficulty,
    statement: data.statement,
    discipline_id: data.discipline_id,
    sub_questions: data.sub_questions?.map((subQ: any) => {
      // Create a new object to avoid modifying the original data
      const mapped: any = {
        statement: subQ.statement,
        answer_justification: subQ.answer_justification,
        type: subQ.type,
        objectives: subQ.objectives?.map((obj: any) => {
          const objMapped: any = {
            text: obj.text,
            is_correct: obj.is_correct,
            order: obj.order,
          };
          // if the objective has an id, include it
          if (obj.id) {
            objMapped.id = obj.id;
          }
          return objMapped;
        }),
      };

      // If the subquestion has an id, include it
      if (subQ.id) {
        mapped.id = subQ.id;
      }

      return mapped;
    }),
  };

  return payload;
}

/**
 * QuestionAPI:
 * CRUD (create, read, update, delete) para perguntas.
 */
export const questionAPI = {
  /**
   * Busca lista paginada de questões.
   */
  getAll: async function (
    search: string = "",
    page: number = 1,
    token: string,
    perPage: number = 10,
    disciplineId?: string
  ): Promise<GetQuestionsResponse> {

    const searchQuery = search ? `&search=${search}` : "";
    const disciplineQuery = disciplineId ? `&discipline_id=${disciplineId}` : "";
    const url = `${API_URI}/admin/private/questions?page=${page}&perPage=${perPage}${searchQuery}${disciplineQuery}`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination
          ? {
              currentPage: data.pagination.current_page,
              totalPages: data.pagination.total_pages,
              itemsPerPage: data.pagination.items_per_page,
              totalItems: data.pagination.total_items,
            }
          : {
              currentPage: 1,
              totalPages: 1,
              itemsPerPage: perPage,
              totalItems: 0,
            };

        const questions: Question[] =
          data.questions?.map((question: any) =>
            mapQuestionResponse(question)
          ) || [];

        return {
          questions,
          pagination,
        };
      })
      .catch((error) => {
        console.error("Error fetching questions:", error);
        throw error;
      });
  },

  /**
   * Busca uma questão pelo ID.
   */
  getById: async function (
    id: string,
    token: string
  ): Promise<QuestionDetails> {
    const url = `${API_URI}/admin/private/questions/${id}`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        if (response.data && response.data.result) {
          const data = response.data.data;
          return mapQuestionDetailsResponse(data);
        } else {
          throw new Error("Question not found or invalid response.");
        }
      })
      .catch((error) => {
        console.error("Error fetching question:", error);
        throw error;
      });
  },

  /**
   * Cria uma nova questão.
   */
  create: async function (
    data: CreateQuestionInput,
    token: string
  ): Promise<Question> {
    const url = `${API_URI}/admin/private/questions`;

    const payload = mapQuestionPayload(data);

    return api
      .request({
        url,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: payload,
      })
      .then((response) => mapQuestionResponse(response.data.data))
      .catch((error) => {
        console.error("Error creating question:", error);
        throw error;
      });
  },

  /**
   * Atualiza uma questão existente.
   */
  update: async function (
    id: string,
    data: UpdateQuestionInput,
    token: string
  ): Promise<Question> {
    const url = `${API_URI}/admin/private/questions/${id}`;
    const payload = mapQuestionPayload(data);

    try {
      const response = await api.request({
        url,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: payload,
      });

      return mapQuestionResponse(response.data.data);
    } catch (error: any) {
      console.error("Error updating Question:", error);

      throw new Error("Error updating Question");
    }
  },

  /**
   * Deleta uma questão pelo ID.
   */
  delete: async function (id: string, token: string): Promise<void> {
    const url = `${API_URI}/admin/private/questions/${id}`;

    return api
      .request({
        url,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error deleting question:", error);
        throw error;
      });
  },

  /**
   * Fetches a paginated list of questions filtered by disciplineId.
   *
   * @param {string} disciplineId - Discipline ID to filter questions.
   * @param {string} token - Authentication token.
   * @param {number} page - Page number for pagination (default is 1).
   * @param {number} perPage - Items per page (default is 10).
   * @param {string} questionType - Type of question to filter (e.g., "objective" or "dissertation").
   * @param {string} [search] - Optional search keyword.
   * @param {string[]} [tags] - Optional array of tags to filter.
   *
   * @returns {Promise<GetQuestionsResponse>} - Returns paginated questions data.
   */
  getByDisciplineId: async function (
    disciplineId: string,
    token: string,
    page: number = 1,
    perPage: number = 10,
    questionType: QuestionType,
    search?: string,
    tags?: string[]
  ): Promise<GetQuestionsDisciplinesResponse> {
    if (!["objective", "dissertative", "set"].includes(questionType)) {
      throw new Error(
        "Invalid question type. Allowed values are 'objective' and 'dissertative'."
      );
    }

    const params = new URLSearchParams({
      page: page.toString(),
      perPage: perPage.toString(),
      question_type: questionType,
    });

    if (search) {
      params.append("search", search);
    }

    if (tags && tags.length > 0) {
      params.append("tags", tags.join(","));
    }

    const url = `${API_URI}/admin/private/questions/discipline/${disciplineId}?${params.toString()}`;

    return api
      .request({
        url,
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination
          ? {
              currentPage: data.pagination.current_page,
              totalPages: data.pagination.total_pages,
              itemsPerPage: data.pagination.items_per_page,
              totalItems: data.pagination.total_items,
            }
          : {
              currentPage: 1,
              totalPages: 1,
              itemsPerPage: perPage,
              totalItems: 0,
            };

        const questions: QuestionDetails[] =
          data.questions?.map((question: any) =>
            mapQuestionDetailsResponse(question)
          ) || [];

        return {
          questions,
          pagination,
        };
      })
      .catch((error) => {
        console.error("Error fetching questions by discipline:", error);
        throw error;
      });
  },
};
